
// "Help right now": the everyday puppy behaviours that wear owners out, each
// with what to do this minute, why it happens, how to prevent it and what not
// to do. Reward-based only (UK guidance: Dogs Trust, RSPCA, Blue Cross, APBC).
// The coach (domain/coach.ts) puts his live state on top — awake time, last
// toilet, the time of day — so the first line fits the moment.

import { cite } from './sources.ts';
import type { SourceRef } from './types.ts';

export type BehaviourId = 'bitey' | 'zoomies' | 'jumping' | 'chewing' | 'crate-crying' | 'velcro' | 'barking' | 'stealing' | 'humping' | 'wont-settle' | 'pen' | 'lead-biting';

export interface Behaviour {
  id: BehaviourId;
  /** As you'd say it: "He's bitey". */
  label: string;
  /** One line: what's going on, kindly. */
  gist: string;
  /** Do this now — short, in order. */
  now: string[];
  why: string;
  prevent: string[];
  avoid: string[];
  /** Games (content/games.ts) and skills (content/training.ts) that help. */
  games?: string[];
  skills?: string[];
  /** When to get help: a trainer or the vet. */
  getHelp?: string;
  /** Where the advice comes from (content/sources.ts). */
  sources?: SourceRef[];
}

export const BEHAVIOURS: Behaviour[] = [
  {
    id: 'bitey',
    sources: cite('dtMouthing', 'rkcSleep', 'rewardBased'),
    label: 'Bitey and wild',
    gist: 'Normal for a young puppy — and nearly always worst when he’s tired, hungry or over-excited.',
    now: [
      'Stop the game and go still and boring — fold your arms and keep your hands still against you (don’t snatch them away), no squealing, no pushing him off.',
      'Give his mouth a job: a tug toy or chew in front of his nose, and praise the moment he takes it.',
      'Ankles or trousers? Stand still — don’t pull your leg or clothes away. Drag a toy along the floor for him to grab instead. Still going? Step behind the baby gate for 10–20 seconds, then come back calmly.',
      'Still crocodiling after a minute? He’s overtired: toilet trip, then into his crate with a stuffed Kong or a safe chew, cover over, and walk away calmly. Most puppies are asleep within minutes.',
    ],
    why: 'Puppies explore with their mouths and play-fight with littermates, who yelp and stop when it hurts — that’s how they learn a soft mouth. People squealing doesn’t work the same way: it usually winds puppies up more. An overtired puppy can’t self-settle, so he gets more frantic, not less: the “witching hour” (often early evening) is usually a missed nap.',
    prevent: [
      'Naps before he needs them: under 12 weeks, about an hour awake then back to sleep (about 75 minutes from 12–16 weeks). Use the “nap due” on Today.',
      'Evenings are the danger zone: get him into his crate for a proper nap around 5–6pm, before the wild spell starts. Keep the last hour before bed to sniffing and chews — no tug or chasing.',
      'Keep a tug or chew within reach of every room, so you always have something better than your hands.',
      'Short, calm games before bed (a few kibbles from his day’s allowance scattered to sniff out, a lick mat while you watch) rather than rough play — his last proper meal about 2 hours before bed.',
      'Play gently with hands off: toys on a rope keep his teeth on the toy, not on you.',
    ],
    avoid: ['Tapping his nose, holding his mouth shut or pinning him — it teaches him hands are scary and often makes biting worse.', 'Waving hands or feet about, or wrestling — it’s an invitation.', 'Telling him off loudly: to a puppy that’s just joining in.'],
    games: ['scatter', 'lick-mat', 'off-switch', 'tug-rules'],
    skills: ['gentle', 'swap', 'settle'],
    getHelp: 'If he’s still bitey past about five months, or the biting is stiff, silent or around food or toys rather than playful, talk to an accredited trainer or behaviourist (APDT, ABTC) — and your vet, to rule out pain.',
  },
  {
    id: 'zoomies',
    sources: cite('dtHyper', 'rkcSleep'),
    label: 'Zoomies',
    gist: 'A burst of happy madness — usually releasing energy after a nap, a bath, a poo or excitement.',
    now: [
      'Make sure the space is safe (rugs, stairs, the sofa edge) and let it run its course — it’s usually over in a minute or two.',
      'Don’t chase him: that turns it into a game. Sit down and be boring.',
      'When he slows: a wee trip. If he’s been up the best part of an hour, straight into his crate with a chew for a nap. If he’s only just woken, scatter a few kibbles to sniff out, then a chew. Sniffing calms.',
    ],
    why: 'Frenetic random activity periods are normal play behaviour. In young puppies they often come just before a crash — a sign he’s due a nap.',
    prevent: ['Toilet trip straight after zoomies (they often come before a wee or poo).', 'Calm sniffing games after baths and before bed.'],
    avoid: ['Chasing him or grabbing for him mid-zoom.', 'Letting zoomies happen on the stair or near the front door.'],
    games: ['scatter', 'lick-mat'],
    skills: ['settle'],
  },
  {
    id: 'jumping',
    sources: cite('dtJumping', 'batterseaGreet'),
    label: 'Jumping up',
    gist: 'He wants to get close to your face — it’s how puppies greet. He jumps because it works.',
    now: [
      'Turn sideways and fold your arms, eyes off him — no talking, no pushing.',
      'The instant all four paws are on the floor: “Yes!” and drop a treat at your feet (low, so he stays down).',
      'Visitors and the stair: put him on his lead or behind the gate until he’s calmer, and have them greet him only when he’s on the floor.',
    ],
    why: 'Jumping up is a natural greeting and gets attention — even “No! Down!” is attention. Reward the floor instead and he’ll choose it.',
    prevent: ['Greet him low and calm, crouching to his level, when you come home.', 'Everyone does the same: no jumping-up cuddles from anyone, ever — or he learns it sometimes pays.'],
    avoid: ['Kneeing him, pushing him off or stepping on his toes.', 'Shouting “down” (save “down” for lying down).'],
    games: ['four-paws'],
    skills: ['four-on-floor', 'calm-greetings', 'sit'],
  },
  {
    id: 'chewing',
    sources: cite('blueCrossChewing', 'pdsaChewing'),
    label: 'Chewing things he shouldn’t',
    gist: 'Puppies need to chew. Right now he’s exploring; from about 12–16 weeks his adult teeth come through (done by about 6–7 months) and his gums get sore. He’s not being naughty.',
    now: [
      'Calmly swap: offer a better chew or a treat, and when he lets go, “Yes!” and give it. Then move the thing out of reach.',
      'Cables: unplug and move them now — electric shock and chewed wires are a real emergency risk.',
      'If he’s swallowed something (sock, hair bobble, part of a toy), call the vet — don’t wait for it to pass.',
    ],
    why: 'Chewing soothes teething gums, relieves boredom and is how puppies explore. What he chews is a question of what’s available.',
    prevent: [
      'Puppy-proof: cables behind the D-Line channel, shoes and socks away, bins closed, nothing he can reach on the coffee table.',
      'Rotate chews so they stay interesting: a rubber chew, a frozen wet flannel (only while you’re watching — take it off him once he starts tearing it) or a carrot for sore gums, a stuffed Kong.',
      'When you can’t watch him, he’s in his pen or crate with something legal to chew.',
    ],
    avoid: ['Chasing him to get it back — that teaches him to grab and run.', 'Telling him off afterwards: he can’t connect it with the chewing.'],
    games: ['swap-game', 'lick-mat'],
    skills: ['swap', 'leave-it'],
  },
  {
    id: 'crate-crying',
    sources: cite('dtFirstNight', 'pdsaNightCry', 'blueCrossNight', 'battersea'),
    label: 'Crying in the crate',
    gist: 'He’s new to being alone. Some crying is normal for the first nights — the aim is to help him feel safe, not to “cry it out”.',
    now: [
      'Night time: if it’s been 2–4 hours, it may be a genuine wee — carry him out, cue word, back to bed, no play.',
      'Otherwise, go to him calmly, sit by the crate, rest a hand on it or talk softly until he settles. Keep it boring.',
      'Crate next to your bed for the first couple of weeks, then move it out bit by bit.',
    ],
    why: 'He’s just left his mum and littermates. Leaving a frightened puppy to cry can make crates scary for good; staying close while he learns it’s safe makes it his den.',
    prevent: ['Make the crate the best place in the flat: treats, the odd meal and a chew before naps, door open in the day.', 'A blanket that smells of his litter, a covered crate, a warm (not hot) water bottle wrapped up.', 'A toilet trip and a calm wind-down before bed, not a play session.'],
    avoid: ['Shutting him in for long stretches in the day.', 'Shouting or banging the crate.', 'Letting him out the moment he barks once he’s settled in — wait for a pause, then go.'],
    games: ['crate-games', 'lick-mat'],
    skills: ['crate-love', 'settle', 'alone-time'],
  },
  {
    id: 'velcro',
    sources: cite('dtSeparation', 'rspcaAlone', 'genPup'),
    label: 'Follows us everywhere / cries when we leave the room',
    gist: 'Normal for a new puppy — and the moment to start teaching him that being apart is safe and boring, so he grows into a dog who copes on his own.',
    now: [
      'If he cries, you’ve gone too far for now. Come back calmly with no fuss (if he goes quiet for a second as you reach the gate, step in then), and make the next absence much shorter.',
      'Next time, make the absence shorter than the one that worried him: a few seconds is plenty to start.',
      'Give him a chew or lick mat behind the baby gate before you step away, so being apart comes with something good.',
    ],
    why: 'He’s just lost his mum and littermates, so sticking close is natural. Separation problems usually grow from never practising being apart — especially in homes where someone is always in — and often show up in adolescence (6–18 months). Cockers are famously people-focused, so early, gentle practice matters.',
    prevent: [
      'Every day, a few short spells apart while one of you is home: him behind the gate with a chew, you pottering about in another room.',
      'Both of you leave the flat together sometimes (for a minute or two at first) — not just one of you, so he doesn’t learn that someone always stays.',
      'Share everything between you — feeding, walks, training, bedtime — so he isn’t attached to just one of you.',
      'Let him nap in his own space in the day, not always on a lap.',
      'Keep goodbyes and hellos low-key, and practise the cues (keys, coat, shoes) without going anywhere.',
      'Plan now for work days: if he’s never left until you go back to the office, it’s a big shock. Build up gradually with Training → Alone time and the camera.',
    ],
    avoid: [
      'Leaving him to “cry it out” — RSPCA advice is never to; it can make being alone scarier.',
      'Being with him every minute for weeks, then suddenly leaving him for hours.',
      'Telling him off for anything you find when you get home. Keep it low-key both ways: in the Generation Pup study, fussing him on return after something had been chewed was linked to about six times the odds of separation problems.',
    ],
    games: ['lick-mat', 'crate-games', 'scatter'],
    skills: ['alone-time', 'settle', 'crate-love'],
    getHelp: 'If he howls, paces, drools, scratches at the door or has accidents only when alone — even for short spells — talk to your vet and an accredited behaviourist early (ABTC, APBC). It’s much easier to help in the first months.',
  },
  {
    id: 'barking',
    sources: cite('dtBarking'),
    label: 'Barking at noises',
    gist: 'Flats are full of strange sounds — the stair, neighbours’ doors, the lift, the street. He’s working out which ones matter.',
    now: [
      'Calmly go and look, then rustle the treat bag and scatter a few treats away from the door when he comes to you.',
      'If he keeps going, take him to another room and give him a chew.',
      'Note what set him off — the same noises usually repeat.',
    ],
    why: 'Puppies bark when something is new or worrying. Pairing the noise with good things (and showing him you’ve handled it) teaches him it’s nothing.',
    prevent: ['Sound games: when the stair door bangs, a treat appears. Over time he looks to you instead of barking.', 'Play recordings of door buzzers and stairwell noises quietly while he eats, turning them up over days.', 'A radio on low when you’re out to blur the sounds.'],
    avoid: ['Shouting “quiet” — to him, you’re barking too.', 'Anti-bark collars of any kind.'],
    games: ['scatter'],
    skills: ['name-game', 'settle'],
  },
  {
    id: 'stealing',
    sources: cite('dtStealing', 'dtResource'),
    label: 'Stealing and running off',
    gist: 'Cockers are born retrievers — picking things up is in the job description. He runs because chase is fun.',
    now: [
      'Don’t chase. Go the other way, make interesting noises, or rustle the treat bag — he’ll come to see.',
      'Offer a swap: say “Drop”, put a treat at his nose, “Yes!” as he lets go, and give the treat. Then give the item back if it’s safe — it’s a game he can’t lose.',
      'If it’s dangerous (medication, a bone, a sock he might swallow), scatter a handful of treats and pick it up while he eats.',
    ],
    why: 'Being chased for an item teaches him to grab faster and guard what he has. Swapping teaches him that bringing things to you pays.',
    prevent: ['Play the swap game daily, so “Drop” is the best word he knows.', 'Praise him when he carries a toy — cockers love a job.', 'Tidy-up habit: socks, tissues and shoes off the floor.'],
    avoid: ['Chasing, cornering or prising his mouth open (unless it’s an emergency).', 'Telling him off when he brings you something — even if it’s your phone.'],
    games: ['swap-game', 'retrieve-game'],
    skills: ['swap', 'retrieve', 'leave-it'],
    getHelp: 'If he stiffens, freezes or growls over things, that’s resource guarding: don’t take things off him, and ask an accredited trainer early — it’s easy to help when he’s young.',
  },
  {
    id: 'humping',
    sources: cite('pdsaHumping'),
    label: 'Humping',
    gist: 'Common in puppies of both sexes and usually about excitement or play, not sex.',
    now: ['Calmly interrupt: call his name and ask for something easy (a sit, a hand touch), then reward.', 'Give him a toy or a sniffy game to bring the excitement down.', 'If it’s a cushion or toy he always uses, put it away for a while.'],
    why: 'Mounting is often an over-excitement or stress outlet, and it can become a habit if it gets a big reaction.',
    prevent: ['Watch for the build-up (overtired, after visitors) and step in with a calm game first.'],
    avoid: ['Laughing or a big fuss — that’s a reward.'],
    games: ['scatter', 'off-switch'],
    skills: ['hand-touch', 'settle'],
  },
  {
    id: 'wont-settle',
    sources: cite('batterseaSettle', 'dtHyper', 'rkcSleep'),
    label: 'Won’t settle',
    gist: 'Puppies need 18–20 hours of sleep a day — but they don’t know how to switch off yet. That’s our job.',
    now: [
      'Toilet trip first, so it’s not that.',
      'Then a short, calm activity: a lick mat, a stuffed Kong or a scatter feed in his pen or crate.',
      'Dim the lights, quiet voices, cover on the crate. Sit nearby if he needs it. Most puppies drop off within 10 minutes once it’s quiet.',
    ],
    why: 'Overstimulation keeps puppies wired. Licking and sniffing are naturally calming; a predictable “nap routine” becomes his cue to sleep.',
    prevent: ['Build a nap routine: same words, same place, same chew.', 'Teach “settle” on a mat when he’s already sleepy, so it’s easy.', 'Count awake time — the app shows when a nap is due.'],
    avoid: ['More play to “tire him out” — puppies get more wired, not more tired.'],
    games: ['lick-mat', 'scatter', 'calm-handling'],
    skills: ['settle', 'crate-love'],
  },
  {
    id: 'pen',
    sources: cite('rkcAlone', 'rspcaAlone', 'genPup', 'genPupSleep', 'blueCrossChewing'),
    label: 'Kicks off in his pen (digging, tearing, barking)',
    gist: 'An awake puppy who’s been fed, toileted and played with isn’t ready to be on his own yet — that’s a skill you teach in seconds, not something he can do at nine weeks. The pen works for awake time when he has something to do and you’re close by; when he’s tired, it’s his crate.',
    now: [
      'Check the clock first. Up for the best part of an hour? He’s tired, and the pen isn’t the answer: toilet, then his crate for a nap with a chew, lights low.',
      'Only just up? Then it isn’t tiredness — he wants to be with you. Let him out at the first second of quiet (not mid-scrabble), and give him 10–15 more minutes of calm time with you: sniffing, a chew, a short training game.',
      'Carpet: lift the edge he’s at or put a washable mat or lino under the pen now. Eating carpet fibres is a vet trip.',
      'Next time, give him a stuffed Kong or lick mat in the pen and stay in the room. Step out for a few seconds and come back before he kicks off.',
    ],
    why: 'He left his litter a week ago and has never been on his own. Awake, fed and full of beans is the hardest moment of his day to be left on his own, so the pen becomes a place he protests in. Awake pen time works when he has something to do (a stuffed Kong, a lick mat, a chew), you’re nearby, and being apart starts tiny: if he gets upset, the step was too big. Games straight before the pen often wind him up rather than tire him out. And if he’s been up a while, the protest is tiredness: that’s crate time.',
    prevent: [
      'His day: wake → wee → food → a few minutes of play and a 2–5 minute training game → calm time in the pen with a Kong or chew while you potter nearby → as he slows down (about an hour after waking at this age), wee, then crate for a nap.',
      'Practise settling in the pen when he’s calm and a bit sleepy (the Royal Kennel Club’s advice for teaching settling): chew in, you nearby, and build up the time you’re out of sight a few seconds at a time.',
      'Give his meals and the best chews in the pen, so it’s where good things happen.',
      'Make the floor pen-proof: lino, vinyl or a washable mat under it, and nothing he can pull through the bars.',
      'Plenty of night sleep matters too: in the Generation Pup study, puppies with 9 or more hours a night had lower odds of separation problems later.',
    ],
    avoid: [
      'Leaving him to “cry it out”, or letting him out mid-tantrum (that teaches him the tantrum opens the gate) — wait for a second of quiet.',
      'Telling him off or fussing him a lot when you go back — keep it low-key both ways.',
      'Only using the pen when you leave the room, so it always predicts being alone.',
    ],
    games: ['lick-mat', 'scatter'],
    skills: ['alone-time', 'settle'],
    getHelp: 'If he’s frantic, drooling, won’t touch even a top treat or has accidents when you’re only out of sight for seconds — and it isn’t easing after a couple of weeks of tiny steps — talk to your vet and an accredited behaviourist (ABTC, APBC).',
  },
  {
    id: 'lead-biting',
    sources: cite('dtLeadRagging'),
    label: 'Biting the lead or harness',
    gist: 'The lead is a moving rope — of course he wants to play tug with it.',
    now: ['Stop walking and go still. Give him a toy to carry or tug instead.', 'Scatter a few treats on the ground — he can’t bite and sniff at once.', 'When he walks on with the lead slack, “Yes!” and treat by your leg.'],
    why: 'Lead biting is usually excitement or frustration — and it’s usually worst when he’s tired.',
    prevent: ['Practise wearing the harness indoors with treats before going out.', 'Carry a tug toy on walks to redirect.', 'Once he’s allowed out, keep early outings short and let him set the pace — “five minutes per month of age” is only a rough guide.'],
    avoid: ['Pulling the lead back — that’s tug.'],
    games: ['tug-rules', 'scatter'],
    skills: ['loose-lead'],
  },
];
